<template>
  <div>
    <wc-page-title title='Receive Payments' subtitle='Cheques, Cash, or eTransfers'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12' md='6'>
          <wc-data-box
            title='Invoice Lookup'
            showHelp
            :helpText='helpText'
          >
            <v-card-text>
              <invoice-lookup
                @newResults='loadNewResults'
                ref='invoiceLookup'
              ></invoice-lookup>
            </v-card-text>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Bill To</th>
                  <th>Project</th>
                  <th>Amount OS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for='r in results' :key='r.id' @click='selectInvoice(r)' :class='r.class'>
                  <td>
                    {{ r.number }}
                    <v-icon small v-if="r.status === 'on_hold'" color='error'>{{$icons.hold.icon}}</v-icon>
                  </td>
                  <td>{{ r.bill_to_display }}</td>
                  <td v-if='r.project_data !== null && r.project_data !== undefined'>
                    {{ r.project_data.name }}
                  </td>
                  <td v-else>
                  </td>
                  <td>{{ r.amount_os | decMoney }}</td>
                </tr>
                <tr v-if='results.length == 0'>
                  <td colspan='3'>No invoices</td>
                </tr>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='6'>
          <wc-data-box
            title='Invoice Details and Payment'
          >
            <wc-two-column-table
              :items='invoiceData'
            ></wc-two-column-table>
            <v-card-actions v-if='selectedInvoice'>
              <v-spacer></v-spacer>
              <v-btn small text color='info' @click='openInvoice(selectedInvoice)'>
                Open Invoice
                <v-icon small right>mdi-open-in-new</v-icon>
              </v-btn>
            </v-card-actions>
          </wc-data-box>
          <br>
          <wc-data-box
            title='Create Payment'
            titleIcon='mdi-credit-card'
            :loadingButtons='postingPmt'
            v-if='accounts.length > 0'
          >
            <v-card-text>
              <v-form v-model='isFormValid' :disabled='postingPmt' ref='form'>
                <v-row>
                  <v-col cols='12'>
                    <wc-datepicker
                      required
                      label='Payment date'
                      :min='this.$store.state.misc.accounting_settings.closed_fiscal_date'
                      v-model='newPmt.date_paid'>
                    </wc-datepicker>
                  </v-col>
                  <v-col cols='12' md='6'>
                    <v-select
                      :rules='[rules.required]'
                      label='Payment method'
                      v-model='newPmt.payment_method'
                      :items='paymentMethods'
                    ></v-select>
                  </v-col>
                  <v-col cols='12' md='6'>
                    <v-text-field
                      :rules='[rules.decimal]'
                      label='Amount paid'
                      v-model='newPmt.amount'
                      reverse
                      prepend-icon='mdi-currency-usd'
                    ></v-text-field>
                  </v-col>
                  <v-col cols='12' class='text-right'>
                    <v-btn
                      depressed
                      color='success'
                      small
                      @click='postPmt'
                      :disabled='!isFormValid || !selectedInvoice'
                      :loading='postingPmt'
                    >
                      <v-icon left small>mdi-checkbox-marked-circle-outline</v-icon>
                      Post payment
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </wc-data-box>
          <br>
          <wc-data-box
            title='Posted payments'
            titleIcon='mdi-checkbox-marked-circle-outline'
          >
            <v-card-text v-if='posted.length === 0'>
              No payments posted yet.
            </v-card-text>
            <v-list dense>
              <template v-for='(p, index) in posted'>
                <v-divider :key='p.id + "_" + index' v-if='index > 0'></v-divider>
                <v-list-item three-line :key='p.id + "_item"'>
                  <v-list-item-content>
                    <v-list-item-title v-if='p.invoice_obj.bill_to'>
                      {{ p.invoice_obj.bill_to_obj.name_display }}
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      No bill to?
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <span class='grey--text'>Invoice #</span> {{ p.invoice_obj.number}}<br>
                      {{ p.amount | decMoney }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-btn
                      color='info'
                      icon
                      @click='openInvoice(p.invoice_obj)'
                    >
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </v-list-item-icon>
                </v-list-item>
              </template>
            </v-list>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import _ from 'lodash';
import InvoiceLookup from '@/modules/accounting/components/InvoiceLookup';
import miscServices from '@/services/misc';
import accountingServices from '../services/accounting';
import trackerAPI from '@/api/tracker';
import accountingAPI from '@/api/accounting';
import moment from 'moment';
import formRules from '@/mixins/formRules';

export default {
  name: 'receive-payments',
  mixins: [formRules],
  data: () => ({
    resetObj: {
      invoice: '',
      date_paid: '',
      payment_method: 'Q',
      amount: 0
    },
    postingPmt: false,
    isFormValid: false,
    selectedInvoice: null,
    loadingInvoice: false,
    newPmt: {},
    accounts: [],
    results: [],
    posted: [],
    helpText: `Enter all, or a portion of the invoice number or the name to whom
    an invoice was issued, and it will retreive the results in the table.<br>
    <br>
    Then click the row of the invoice against which you want to receive a payment, and complete
    the fields on the right
    `
  }),
  computed: {
    invoiceData () {
      if (this.selectedInvoice) {
        return [
          { text: 'Bill To', value: this.selectedInvoice.bill_to_obj ? this.selectedInvoice.bill_to_obj.name_display : 'No bill to?' },
          { text: 'Numbese tr', value: this.selectedInvoice ? this.selectedInvoice.number : '' },
          { text: 'Date Sent', value: this.selectedInvoice ? this.$options.filters.shortDate(this.selectedInvoice.date_sent) : ''},
          { text: 'Due Date', value: this.selectedInvoice ? this.$options.filters.shortDate(this.selectedInvoice.date_due) : ''},
          { text: 'Amount Outstanding', value: this.selectedInvoice ? this.$options.filters.decMoney(this.selectedInvoice.amount_os) : '' }
        ]
      }
      return [
        { text: 'Bill To', value: '' },
        { text: 'Number', value: '' },
        { text: 'Date Sent', value: ''},
        { text: 'Due Date', value: ''},
        { text: 'Amount Outstanding', value: '' }
      ]
    },
    paymentMethods () {
      return _.filter(this.$store.state.misc.globalOptions.paymentTypes, pt => pt.value != "V")
    }
  },
  mounted () {
    this.getData()
    this.resetPmt ()
  },
  methods: {
    resetPmt () {
      this.newPmt = _.cloneDeep(this.resetObj)
      this.newPmt.date_paid = moment().format('YYYY-MM-DD').toString()
      let acct = _.find(this.accounts, function(a) { return a.is_default === true})
      if (acct) {
        this.newPmt.account = acct.id
      }
    },
    loadNewResults (results) {
      this.results = results
    },
    async getData () {
      this.accounts = await accountingAPI.getDepositAccounts()
      this.resetPmt()
    },
    async selectInvoice (row) {
      this.results = this.results.map(function(el) {
        let o = Object.assign({}, el)
        o.class = ''
        return o
      })
      row.class = 'info'
      this.results = miscServices.replaceObject(this.results, row)
      this.loadingInvoice = true
      this.selectedInvoice = await accountingAPI.getInvoice(row.id)
      this.newPmt.invoice = this.selectedInvoice.id
      this.newPmt.date_paid = moment().format('YYYY-MM-DD').toString()
      this.newPmt.amount = this.selectedInvoice.amount_os
    },
    openInvoice (inv) {
      const link = document.createElement('a')
      const invURL = this.$router.resolve({ name: 'InvoiceOverview', params: { id: inv.id } })
      link.target = '_blank'
      link.href = invURL.href
      link.click()
    },
    async postPmt () {
      this.postingPmt = true;
      let tmp = await accountingAPI.createInvoicePayment(this.newPmt);
      let inv = _.cloneDeep(tmp.invoice_obj);
      await accountingServices.updateInvoiceTotals(inv.id, inv);

      const tracker_data = {
            'description': 'Recorded payment against invoice of ' + this.newPmt.amount,
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString()
        }
        const tracker = {
            'tracker_data': tracker_data,
            'invoice': this.selectedInvoice.id
        }
      const res = await trackerAPI.createInvoiceTracker(tracker);
      this.$emit('newAction', res);

      this.posted.push(tmp);
      this.selectedInvoice = null;
      this.$refs.invoiceLookup.clearSearch();
      this.results = [];
      this.resetPmt();
      this.$refs.form.resetValidation();
      this.postingPmt = false;
    }
  },
  components: {
    'invoice-lookup': InvoiceLookup
  }
}
</script>
